import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, type ChatMessage, type ChatSession } from "../api/types";
import { createReportChat, deleteReportChat, listReportChats } from "../api/reports";
import { useChat, useSessionRun } from "../store/chat";
import { useReports } from "../store/reports";
import { useLocale } from "../hooks/useLocale";
import { MessageBubble } from "./MessageBubble";
import { StepTimeline } from "./StepTimeline";
import { CancelButton } from "./CancelButton";
import { ClarifyPrompt } from "./ClarifyPrompt";
import styles from "./ReportEditPanel.module.css";

interface Props {
  reportId: number;
  onClose: () => void;
  // Called after a successful Save so the parent can re-run the report and reflect new data.
  onSaved: () => void;
}

interface OptimisticUser {
  text: string;
  ts: number;
}

function chatLabel(c: ChatSession, t: (k: string, p?: Record<string, string | number>) => string): string {
  return c.name?.trim() || t("chat.untitled", { id: c.id });
}

/**
 * Edit panel for a report. A report can have several editor chats; this rail
 * lists them, resumes the most recent on open (or creates one if none exist),
 * and lets the user start a new editor chat. Each assistant выгрузка carries a
 * "Save to report" button that overwrites the report from that message — there
 * is no chat-level Save anymore (saving is tied to a specific выгрузка).
 */
export function ReportEditPanel({ reportId, onClose, onSaved }: Props) {
  const { t, tOr } = useLocale();

  const messages = useChat((s) => s.messages);
  const selectSession = useChat((s) => s.selectSession);
  const loadingDetail = useChat((s) => s.loadingDetail);

  const sendMessage = useChat((s) => s.sendMessage);
  const cancelRun = useChat((s) => s.cancelRun);

  const saveReport = useReports((s) => s.save);
  const loadReportDetail = useReports((s) => s.loadDetail);
  // The report's current version is the optimistic-CC stamp we echo back as
  // expected_version on save; on conflict we reload to pick the new value.
  const reportVersion = useReports((s) => s.current?.version);

  const [chats, setChats] = useState<ChatSession[]>([]);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [optimisticUser, setOptimisticUser] = useState<OptimisticUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const composerRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Per-chat run projection; the hook also keeps the chat's server-side run
  // observed while the panel is open (detach on unmount/switch only closes
  // this client's subscription — the run itself keeps going).
  const run = useSessionRun(activeChatId);

  // Resolve the report's editor chats: resume the most recent, or create one
  // if none exist.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        let list = await listReportChats(reportId);
        if (list.length === 0) {
          const created = await createReportChat(reportId);
          list = [created];
        }
        if (cancelled) return;
        setChats(list);
        setActiveChatId(list[0].id);
        await selectSession(list[0].id);
      } catch (err) {
        if (cancelled) return;
        console.error("load editor chats failed", err);
        setError(t("common.loading"));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Auto-grow textarea to fit draft, clamped by min/max-height in CSS.
  useEffect(() => {
    const ta = composerRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [draft]);

  // Stick to the bottom whenever the message list grows or steps tick.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight });
  }, [messages.length, run.steps.length, optimisticUser]);

  // Clear the optimistic user-bubble when the run reaches a terminal state.
  useEffect(() => {
    if (
      run.status === "idle" ||
      run.status === "error" ||
      run.status === "cancelled"
    ) {
      setOptimisticUser(null);
    }
  }, [run.status]);

  const isStreaming = run.status === "streaming";
  const isAwaitingClarify = run.status === "awaiting_clarify";
  const busy = isStreaming || isAwaitingClarify;

  function buildOptimisticMessage(): ChatMessage | null {
    if (!optimisticUser || activeChatId == null) return null;
    return {
      id: -optimisticUser.ts,
      session_id: activeChatId,
      tenant_id: 0,
      role: "user",
      content: optimisticUser.text,
      created_at: new Date(optimisticUser.ts).toISOString(),
    };
  }

  function dispatchStream(text: string) {
    if (activeChatId == null) return;
    setError(null);
    setOptimisticUser({ text, ts: Date.now() });
    void sendMessage(text, activeChatId);
  }

  function submit() {
    const text = draft.trim();
    if (!text || busy || activeChatId == null) return;
    setDraft("");
    dispatchStream(text);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  async function onSelectChat(id: number) {
    if (id === activeChatId || busy) return;
    setError(null);
    setOptimisticUser(null);
    setActiveChatId(id);
    try {
      await selectSession(id);
    } catch (err) {
      console.error("select editor chat failed", err);
      setError(t("common.loading"));
    }
  }

  async function onNewChat() {
    if (busy) return;
    setError(null);
    try {
      const created = await createReportChat(reportId);
      setChats((prev) => [created, ...prev]);
      setOptimisticUser(null);
      setActiveChatId(created.id);
      await selectSession(created.id);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      } else {
        setError(t("common.loading"));
      }
    }
  }

  async function onDeleteChat(id: number) {
    if (busy) return;
    if (!window.confirm(t("chat.delete_confirm"))) return;
    setError(null);

    const remaining = chats.filter((c) => c.id !== id);
    setChats(remaining);

    // If we deleted the active chat, switch to the next available one (the
    // leftmost / newest), or fall back to creating a fresh chat when none remain.
    if (id === activeChatId) {
      setOptimisticUser(null);
      if (remaining.length > 0) {
        setActiveChatId(remaining[0].id);
        void selectSession(remaining[0].id);
      } else {
        setActiveChatId(null);
      }
    }

    try {
      await deleteReportChat(id);
      if (id === activeChatId && remaining.length === 0) {
        const created = await createReportChat(reportId);
        setChats([created]);
        setActiveChatId(created.id);
        await selectSession(created.id);
      }
    } catch (err) {
      console.error("delete editor chat failed", err);
      // Roll back from the server's truth so the failed deletion re-appears.
      try {
        const list = await listReportChats(reportId);
        setChats(list);
      } catch {
        /* ignore secondary failure */
      }
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      } else {
        setError(t("common.loading"));
      }
    }
  }

  async function onSaveToReport(messageId: number) {
    if (saving || busy) return;
    if (reportVersion == null) {
      // No loaded report = nothing to save against. Shouldn't happen in
      // practice (the panel mounts inside a loaded report page) but guard so
      // we never send expected_version=0 (which the server rejects as missing).
      setError(t("report.save_failed"));
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await saveReport(reportId, messageId, reportVersion);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === "version_conflict") {
        // Another editor chat saved between our read and write. Pull the new
        // definition so the next save's expected_version is fresh, then ask
        // the user to retry.
        try {
          await loadReportDetail(reportId);
        } catch {
          /* secondary failure — primary message still surfaces */
        }
        setError(tOr("errors.version_conflict", "errors.unknown"));
      } else if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      } else {
        setError(t("report.save_failed"));
      }
    } finally {
      setSaving(false);
    }
  }

  async function onCancel() {
    if (activeChatId == null) return;
    try {
      await cancelRun(activeChatId);
    } catch (err) {
      console.warn("cancel failed", err);
    }
  }

  const optimisticMsg = buildOptimisticMessage();

  return (
    <aside className={styles.panel}>
      <header className={styles.header}>
        <h3 className={styles.title}>{t("report.edit_panel_title")}</h3>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={() => void onNewChat()}
            disabled={busy}
          >
            {t("report.new_chat")}
          </button>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>
      </header>

      {chats.length > 1 && (
        <div className={styles.chatTabs}>
          {chats.map((c) => (
            <div
              key={c.id}
              className={`${styles.chatTab} ${c.id === activeChatId ? styles.chatTabActive : ""}`}
            >
              <button
                type="button"
                className={styles.chatTabLabel}
                onClick={() => void onSelectChat(c.id)}
                disabled={busy}
                title={chatLabel(c, t)}
              >
                {chatLabel(c, t)}
              </button>
              <button
                type="button"
                className={styles.chatTabClose}
                onClick={() => void onDeleteChat(c.id)}
                disabled={busy}
                aria-label={t("chat.delete")}
                title={t("chat.delete")}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.scroll} ref={scrollRef}>
        {loadingDetail && messages.length === 0 ? (
          <p className={styles.loading}>{t("common.loading")}</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              onSaveToReport={busy ? undefined : onSaveToReport}
              saveDisabled={saving || busy}
            />
          ))
        )}

        {optimisticMsg && <MessageBubble message={optimisticMsg} />}

        {(isStreaming || isAwaitingClarify) && (
          <StepTimeline steps={run.steps} active={isStreaming} />
        )}

        {isAwaitingClarify && run.clarifyQuestion && (
          <ClarifyPrompt
            question={run.clarifyQuestion}
            onSubmit={(answer) => dispatchStream(answer)}
          />
        )}

        {error && <p className={styles.error}>{error}</p>}
      </div>

      <form className={styles.composer} onSubmit={onSubmit}>
        <textarea
          ref={composerRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("chat.placeholder")}
          rows={1}
          maxLength={20000}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        {busy ? (
          <CancelButton onCancel={() => void onCancel()} />
        ) : (
          <button type="submit" className={styles.sendBtn} disabled={!draft.trim()}>
            {t("chat.send")}
          </button>
        )}
      </form>
    </aside>
  );
}
